import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";

import {
  StatusMessagePosition,
  StatusMessageType,
} from "components/StatusMessageQueue/StatusMessageQueueProvider";
import useStatusMessageQueue from "components/StatusMessageQueue/hooks/useStatusMessageQueue";

import { ReactQueryKeysAccount } from "../queryKeys";
import { WorkspaceEngine, WorkspaceEngineStatus } from "./engine.types";
import {
  clearStartAbortController,
  setStartAbortController,
} from "./startAbortRegistry";
import { startWorkspaceEngine } from "./startWorkspaceEngine";

export const useStartWorkspaceEngine = () => {
  const queryClient = useQueryClient();
  const { putStatusMessage } = useStatusMessageQueue();
  const startControllersRef = useRef<Map<string, AbortController>>(new Map());
  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: async (engineName: string) => {
      // Abort any previous start for this engine if still pending
      const prev = startControllersRef.current.get(engineName);
      if (prev) {
        prev.abort("Abort previous engine start");
      }

      const abortController = new AbortController();
      startControllersRef.current.set(engineName, abortController);
      setStartAbortController(engineName, abortController);

      try {
        await startWorkspaceEngine(engineName, {
          signal: abortController.signal,
        });
      } finally {
        // Clean up controller after completion or abort
        const current = startControllersRef.current.get(engineName);
        if (current === abortController) {
          startControllersRef.current.delete(engineName);
        }
        clearStartAbortController(engineName, abortController);
      }
    },
    onMutate: async (engineName: string) => {
      // Cancel any in-flight queries
      await queryClient.cancelQueries({
        queryKey: [ReactQueryKeysAccount.workspaceEngines],
        exact: false,
      });

      // Update all matching queries with optimistic state
      const queries = queryClient.getQueriesData<WorkspaceEngine[]>({
        queryKey: [ReactQueryKeysAccount.workspaceEngines],
        exact: false,
      });

      queries.forEach(([queryKey]) => {
        queryClient.setQueryData<WorkspaceEngine[]>(queryKey, engines => {
          return (engines || []).map(engine => {
            if (engine.engineName === engineName) {
              return {
                ...engine,
                status: WorkspaceEngineStatus.STARTING,
              };
            }
            return engine;
          });
        });
      });
    },
    onError: async (error: Error) => {
      if (error.name === "AbortError") {
        return;
      }

      putStatusMessage({
        type: StatusMessageType.Error,
        message: error.message,
        options: {
          id: "start",
          insertToPosition: StatusMessagePosition.Top,
          autoRemove: true,
        },
      });

      // On error, invalidate to refetch fresh data
      await queryClient.invalidateQueries({
        queryKey: [ReactQueryKeysAccount.workspaceEngines],
        exact: false,
      });
    },
  });

  return {
    mutate,
    isLoading,
  };
};
